<template>
  <div class="paper-upload">
    <form @submit.prevent="handleSubmit">
      <!-- Drag & Drop / File Attachment Input -->
      <div class="paper-upload__form-group">
        <div
          class="paper-upload__dropzone"
          :class="{
            'paper-upload__dropzone--invalid': errors.has('file'),
            'paper-upload__dropzone--dragover': isDragging,
            'paper-upload__dropzone--has-file': Boolean(file)
          }"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
          @click="triggerFileInput"
        >
          <!-- Hidden accessible file input -->
          <input
            id="upload_attachment"
            ref="fileInput"
            type="file"
            name="file"
            accept=".pdf,application/pdf"
            v-validate="'required'"
            data-vv-as="file"
            class="paper-upload__file-input"
            @change="handleAttachmentUpload"
          />

          <!-- Empty State -->
          <div v-if="!file" class="paper-upload__dropzone-content">
            <i class="fa fa-cloud-upload paper-upload__icon" aria-hidden="true"></i>
            <span>Drag & drop PDF here, or <strong class="paper-upload__browse-text">Browse</strong></span>
          </div>

          <!-- File Uploaded Preview & Delete Button -->
          <div v-else class="paper-upload__file-preview">
            <div class="paper-upload__file-info">
              <i class="fa fa-file-pdf-o paper-upload__icon paper-upload__icon--pdf" aria-hidden="true"></i>
              <span class="paper-upload__filename">{{ uploadAttachmentName }}</span>
            </div>
            <button
              type="button"
              class="paper-upload__delete-btn"
              title="Remove file"
              aria-label="Remove uploaded file"
              @click.stop="removeFile"
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <small v-if="errors.has('file')" class="paper-upload__error">
          {{ errors.first('file') }}
        </small>

        <!-- Upload Progress Bar -->
        <div v-if="uploadProgress > 0 && submitted" class="paper-upload__progress">
          <h4 class="paper-upload__progress-status">
            {{ uploadProgress === 100 ? 'Finishing...' : 'Uploading Files...' }}
          </h4>
          <div class="paper-upload__progress-track">
            <div
              class="paper-upload__progress-fill"
              :style="{ width: uploadProgress + '%' }"
            ></div>
          </div>
          <span class="paper-upload__progress-text">{{ uploadProgress }}%</span>
        </div>
      </div>

      <!-- PDF Password (Encrypted Files) -->
      <div v-if="encryptedToggle" class="paper-upload__form-group">
        <label for="pdf-password" class="paper-upload__label">PDF Password</label>
        <input
          id="pdf-password"
          v-model="pdf_password"
          v-validate="'required'"
          type="text"
          autocomplete="off"
          data-lpignore="true"
          data-form-type="other"
          name="pdf_password"
          class="paper-upload__input"
          :class="{ 'paper-upload__input--invalid': errors.has('pdf_password') }"
          data-vv-as="pdf password"
          placeholder="Enter password to unlock PDF"
          @input="clearPasswordError"
        />
        <small v-if="errors.has('pdf_password')" class="paper-upload__error">
          {{ errors.first('pdf_password') }}
        </small>
      </div>

      <!-- Incorrect Password Feedback -->
      <div v-if="passwordIncorrectState" class="paper-upload__form-group">
        <span class="paper-upload__error-alert">Password is incorrect</span>
      </div>

      <!-- Submit Button -->
      <div class="paper-upload__actions">
        <button
          type="submit"
          class="paper-upload__btn-submit"
          :disabled="submitted"
        >
          <span v-if="submitted">Uploading...</span>
          <span v-else>Upload</span>
        </button>
      </div>
    </form>

    <!-- Update PP Details Modal -->
    <update-pp-details
      v-if="updatePaperPileDetailPopupState"
      :paper-update-i-n-f-o="paperUpdateInfo"
    ></update-pp-details>
  </div>
</template>

<script>
import axios from 'axios';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';
import UpdatePpDetails from '../../update-paperpile-details';

const paperUploadFromLocal = root.pdfPaperPileApiUrl.link;

export default {
  name: 'AddPaperFromLocal',
  components: {
    'update-pp-details': UpdatePpDetails
  },
  data() {
    return {
      submitted: false,
      file: null,
      uploadAttachmentName: '',
      topic: '',
      updatePaperPileDetailPopupState: false,
      paperUpdateInfo: null,
      encryptedToggle: false,
      pdf_password: '',
      passwordIncorrectState: false,
      uploadProgress: 0,
      isDragging: false
    };
  },
  mounted() {
    bus.$on('updatePaperPileDetailPopupStateBus', (data) => {
      this.updatePaperPileDetailPopupState = data;
    });
  },
  beforeDestroy() {
    bus.$off('updatePaperPileDetailPopupStateBus');
  },
  methods: {
    triggerFileInput() {
      if (!this.file && this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    processFile(file) {
      if (!file) {
        this.removeFile();
        return;
      }

      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Please upload a valid PDF file');
        return;
      }

      this.file = file;
      this.uploadAttachmentName = file.name;
      this.topic = file.name.replace(/\.[^/.]+$/, '');

      // Assign file to input and safely dispatch native event
      if (this.$refs.fileInput) {
        try {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          this.$refs.fileInput.files = dataTransfer.files;
        } catch (e) {
          // Fallback for environments where DataTransfer constructor is restricted
        }
      }

      // Clear any prior validation error for this field
      if (this.errors) {
        this.errors.remove('file');
      }

      // Check encryption
      const reader = new FileReader();
      reader.onload = (e) => {
        const contents = e.target.result;
        this.encryptedToggle = typeof contents === 'string' && contents.indexOf('/Encrypt') !== -1;
      };
      reader.readAsText(file);
    },

    handleAttachmentUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    removeFile() {
      this.file = null;
      this.uploadAttachmentName = '';
      this.topic = '';
      this.encryptedToggle = false;
      this.pdf_password = '';
      this.passwordIncorrectState = false;

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }

      if (this.errors) {
        this.errors.remove('file');
      }
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const droppedFiles = event.dataTransfer && event.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        this.processFile(droppedFiles[0]);
      }
    },

    clearPasswordError() {
      if (this.passwordIncorrectState) {
        this.passwordIncorrectState = false;
      }
    },

    async handleSubmit() {
      // Manual fallback validation if file is missing
      if (!this.file) {
        this.errors.add({
          field: 'file',
          msg: 'The file field is required.'
        });
        return;
      }

      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.submitted = true;
      this.passwordIncorrectState = false;

      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('title', this.topic);
      formData.append('file_name', this.uploadAttachmentName);
      formData.append('is_favourite', false);
      formData.append('is_uploaded', true);

      if (this.pdf_password) {
        formData.append('pdf_password', this.pdf_password);
      }

      try {
        const res = await axios.post(paperUploadFromLocal, formData, {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total > 0) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
            }
          }
        });

        const successMsg = (res.data && res.data.msg) || 'File uploaded successfully';
        toast.success(successMsg);
        this.$router.push({ name: 'paperPiles' });
        this.resetForm();
      } catch (err) {
        this.handleUploadError(err);
      } finally {
        this.submitted = false;
      }
    },

    handleUploadError(err) {
      this.uploadProgress = 0;
      const status = err.response ? err.response.status : err.status;
      const responseData = err.response ? err.response.data : err.data;

      if (status === 406) {
        toast.error('The PDF file already exists');
      } else if (status === 409) {
        this.passwordIncorrectState = true;
        toast.error('PDF password is incorrect');
      } else if (status === 417) {
        toast.error('Expectation Failed');
      } else {
        const errorMsg =
          (responseData && (responseData.user_msg || responseData.msg)) ||
          'An error occurred';
        toast.error(errorMsg);
      }
    },

    openUpdateModal(data) {
      this.paperUpdateInfo = data;
      this.updatePaperPileDetailPopupState = true;
      document.body.classList.add('body_right_scroll_remove');
    },

    resetForm() {
      this.removeFile();
      this.uploadProgress = 0;
      this.isDragging = false;
      this.$validator.reset();
    }
  }
};
</script>

<style lang="scss" scoped>
$theme-gold: #ffd400;
$theme-gold-muted: #ffc107;
$border-translucent: rgba(255, 255, 255, 0.1);
$bg-translucent: rgba(255, 255, 255, 0.03);
$error-color: #dc3545;
$text-light: #ffffff;

.paper-upload {
  letter-spacing: 0.5px;
  width: 100%;

  &__form-group {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 1rem;
    color: $text-light;
    margin-bottom: 8px;
    font-weight: 500;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 10px 16px;
    font-size: 1rem;
    color: $theme-gold;
    background: $bg-translucent;
    border: 1px solid $border-translucent;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    -webkit-text-security: disc; // Masks input text as password dots in Chrome/Safari/Edge
    font-family: text-security-disc, -apple-system, sans-serif;

    &:focus {
      border-color: $theme-gold;
    }

    &--invalid {
      border-color: $error-color !important;
    }
  }

  &__dropzone {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 160px;
    padding: 24px;
    background: $bg-translucent;
    border: 2px dashed $border-translucent;
    border-radius: 10px;
    cursor: pointer;
    text-align: center;
    transition: all 0.25s ease;

    &:hover,
    &--dragover {
      border-color: $theme-gold;
      background: rgba(255, 212, 0, 0.05);

      .paper-upload__icon {
        color: $theme-gold;
        transform: scale(1.1);
      }

      .paper-upload__browse-text {
        text-decoration: underline;
      }
    }

    &--has-file {
      cursor: default;
      border-style: solid;
      border-color: rgba(255, 255, 255, 0.2);
    }

    &--invalid {
      border-color: $error-color !important;
    }
  }

  /* Accessible off-screen input that keeps DOM registration intact */
  &__file-input {
    position: absolute;
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    z-index: -1;
  }

  &__dropzone-content {
    font-size: 1.1rem;
    color: $text-light;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    pointer-events: none;
  }

  // Uploaded File Preview Box
  &__file-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 480px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 12px 16px;
    gap: 12px;
  }

  &__file-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  &__filename {
    font-size: 1rem;
    color: $text-light;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__delete-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(220, 53, 69, 0.15);
    border: 1px solid rgba(220, 53, 69, 0.4);
    color: $error-color;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $error-color;
      color: #ffffff;
      transform: scale(1.05);
    }
  }

  &__browse-text {
    color: $theme-gold;
    font-weight: 600;
  }

  &__icon {
    font-size: 2.2rem;
    color: rgba(255, 255, 255, 0.6);
    transition: all 0.25s ease;

    &--pdf {
      font-size: 1.6rem;
      color: $theme-gold;
    }
  }

  &__error {
    color: $error-color;
    font-size: 0.875rem;
    margin-top: 6px;
  }

  &__error-alert {
    color: $error-color;
    font-size: 0.95rem;
    font-weight: 500;
  }

  // Progress Bar
  &__progress {
    margin-top: 20px;
    text-align: center;
  }

  &__progress-status {
    color: $theme-gold-muted;
    font-size: 0.9rem;
    margin-bottom: 8px;
  }

  &__progress-track {
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background-color: $theme-gold-muted;
    transition: width 0.2s ease;
  }

  &__progress-text {
    display: inline-block;
    margin-top: 6px;
    font-size: 0.85rem;
    color: $theme-gold-muted;
  }

  // Actions
  &__actions {
    text-align: center;
    margin-top: 30px;
  }

  &__btn-submit {
    min-width: 130px;
    padding: 10px 24px;
    font-size: 1rem;
    font-weight: 600;
    color: $theme-gold;
    background: transparent;
    border: 2px solid $theme-gold;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: $theme-gold;
      color: #1a1a1a;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>